import { useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const MAX_DIM = 10000;
const WEBP_QUALITY = 0.88;
const JPEG_FALLBACK_QUALITY = 0.82;
const CHUNK_SIZE = 512 * 1024;
const USE_CHUNKS_THRESHOLD_MB = 0.3;
const MAX_RETRY_ATTEMPTS = 5;
const CHUNK_TIMEOUT = 60000;
const INTER_CHUNK_DELAY = 300;

function compressImage(file) {
  return new Promise((resolve) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const aspectRatio = width / height;
      if (width > MAX_DIM || height > MAX_DIM) {
        const scale = MAX_DIM / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ file: new File([blob], "map_overlay.webp", { type: "image/webp" }), aspectRatio });
          } else {
            canvas.toBlob(
              (jpegBlob) => resolve({
                file: jpegBlob
                  ? new File([jpegBlob], "map_overlay.jpg", { type: "image/jpeg" })
                  : file,
                aspectRatio
              }),
              "image/jpeg",
              JPEG_FALLBACK_QUALITY
            );
          }
        },
        "image/webp",
        WEBP_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ file, aspectRatio: 1 });
    };
    img.src = url;
  });
}

function calculateBounds(mapCenter, aspectRatio, zoom) {
  // Spread in degrees based on zoom level — smaller spread at higher zoom
  const baseSpread = 0.01;
  const zoomFactor = Math.pow(2, 15 - (zoom || 15));
  const latSpread = baseSpread * zoomFactor;
  const lngSpread = latSpread * aspectRatio;

  const south = mapCenter.lat - latSpread / 2;
  const north = mapCenter.lat + latSpread / 2;
  const west = mapCenter.lng - lngSpread / 2;
  const east = mapCenter.lng + lngSpread / 2;

  return [[south, west], [north, east]];
}

async function uploadChunked(optimizedFile, eventId, token, bounds) {
  const totalChunks = Math.ceil(optimizedFile.size / CHUNK_SIZE);

  const initFormData = new FormData();
  initFormData.append("event_id", eventId);
  initFormData.append("filename", "map_overlay.webp");
  initFormData.append("total_chunks", totalChunks);
  initFormData.append("total_size", optimizedFile.size);
  if (bounds) {
    initFormData.append("bounds", JSON.stringify(bounds));
  }

  const initRes = await axios.post(`${API}/map-overlay/init-chunk-upload`, initFormData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const uploadId = initRes.data.upload_id;

  let lastChunkRes = null;
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, optimizedFile.size);
    const chunk = optimizedFile.slice(start, end);

    let success = false;
    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS && !success; attempt++) {
      try {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("chunk", chunk);
        const res = await axios.post(`${API}/map-overlay/upload-chunk`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: CHUNK_TIMEOUT,
        });
        lastChunkRes = res.data;
        success = true;
      } catch (err) {
        if (attempt < MAX_RETRY_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt - 1)));
        } else {
          throw err;
        }
      }
    }

    if (i < totalChunks - 1) {
      await new Promise((r) => setTimeout(r, INTER_CHUNK_DELAY));
    }

    const progress = Math.round(((i + 1) / totalChunks) * 100);
    if (progress % 10 === 0 || i === totalChunks - 1) {
      toast.info(`Upload: ${progress}% (${i + 1}/${totalChunks})`);
    }
  }

  return lastChunkRes;
}

async function uploadStandard(file, eventId, token, bounds) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("event_id", eventId);
  if (bounds) {
    fd.append("bounds", JSON.stringify(bounds));
  }
  await axios.post(`${API}/map-overlay`, fd, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
  });
}

export function useChunkedUpload({ eventId, token, onUploadComplete, mapRef }) {
  const handleFileUpload = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (file.type === "application/pdf") {
        toast.error("PDF understøttes ikke. Upload venligst et billede (PNG/JPG).");
        return;
      }
      if (!eventId) {
        toast.error("Intet event valgt");
        return;
      }

      const fileSizeMB = file.size / 1024 / 1024;

      try {
        toast.info(`Optimerer kort (${fileSizeMB.toFixed(1)}MB)...`);
        const { file: optimized, aspectRatio } = await compressImage(file);
        const optimizedMB = optimized.size / 1024 / 1024;
        toast.info(`Komprimeret: ${fileSizeMB.toFixed(1)}MB → ${optimizedMB.toFixed(1)}MB`);

        // Calculate bounds from current map view + image aspect ratio
        let bounds = null;
        if (mapRef?.current) {
          const map = mapRef.current;
          const center = map.getCenter();
          const zoom = map.getZoom();
          bounds = calculateBounds(center, aspectRatio, zoom);
        }

        if (optimizedMB > USE_CHUNKS_THRESHOLD_MB) {
          toast.info(`Uploader kort (${optimizedMB.toFixed(1)}MB) i dele...`);
          const result = await uploadChunked(optimized, eventId, token, bounds);

          if (result?.overlay_ready) {
            await onUploadComplete?.();
            toast.success(`Kort uploadet! (${result.final_size_mb}MB)`);
          } else {
            toast.error("Upload fuldført men kort blev ikke gemt. Genindlæs siden.");
          }
        } else {
          await uploadStandard(optimized, eventId, token, bounds);
          await onUploadComplete?.();
          toast.success("Kort uploadet!");
        }
      } catch (error) {
        console.error("Upload fejl:", error);
        toast.error(`Fejl: ${error.response?.status || ""} ${error.response?.data?.detail || error.message}`);
      }
    },
    [eventId, token, onUploadComplete, mapRef]
  );

  return { handleFileUpload };
}
